import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { isDefined } from '@morpho-org/morpho-ts';
import { Avatar } from '@morpho-org/ui-v6/client';
import { CardText, Skeleton, V1Badge } from '@morpho-org/ui-v6/server';
import { Icon, Separator, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { ArrowRight } from '@repo/ui/icons';
import { abbreviateAddress } from '@repo/utils';
import IconRewardHover from '@/components/common/IconRewardHover/IconRewardHover';
import { SimulationEstimateBadge } from '@/components/common/SimulationEstimateBadge';
import { useVaultV1RecapFormatting } from './hooks/useVaultV1RecapFormatting';
import { RecapContainer } from './RecapContainer';
import RecapInteraction from './RecapInteraction';
import { RecapApyRowSkeleton, RecapInteractionSkeleton, RecapPositionRowSkeleton, RecapVaultHeaderSkeleton } from './RecapSkeletons';
export interface VaultV1RecapProps {
  type: 'deposit' | 'withdraw';
  amount: bigint;
  initialPosition: bigint;
  finalPosition: bigint | undefined;
  vaultAddress: Address;
  /** Shows a skeleton in place of the position-after value while the EVM simulation resolves. */
  isFinalPositionLoading?: boolean;
  /** Position-after is an estimate: a simulation ran but couldn't verify the outcome. */
  isSimulationFallback?: boolean;
}
const VAULT_OPERATION_LABEL = {
  deposit: 'Deposit',
  withdraw: 'Withdraw'
};
const VaultV1Recap = t0 => {
  const $ = _c(76);
  const {
    type,
    amount,
    initialPosition,
    finalPosition,
    vaultAddress,
    isFinalPositionLoading,
    isSimulationFallback
  } = t0;
  let t1;
  if ($[0] !== finalPosition || $[1] !== initialPosition || $[2] !== vaultAddress) {
    t1 = {
      vaultAddress,
      initialPosition,
      finalPosition
    };
    $[0] = finalPosition;
    $[1] = initialPosition;
    $[2] = vaultAddress;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    vaultV1,
    vaultAsset,
    apy,
    performanceFee,
    netApy,
    points,
    rewards,
    formattedNetApy,
    formattedInitialPosition,
    detailedFormattedInitialPosition,
    formattedFinalPosition,
    detailedFormattedFinalPosition,
    loading
  } = useVaultV1RecapFormatting(t1);
  if (loading) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <RecapVaultHeaderSkeleton />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    const t3 = VAULT_OPERATION_LABEL[type];
    let t4;
    if ($[5] !== t3) {
      t4 = <RecapInteractionSkeleton label={t3} />;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Separator />;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="gap-sm flex flex-col items-stretch"><RecapPositionRowSkeleton /><RecapApyRowSkeleton /></div>;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== t4) {
      t7 = <RecapContainer className="gap-lg flex-col">{t2}<div className="gap-sm flex flex-col items-stretch">{t4}{t5}{t6}</div></RecapContainer>;
      $[9] = t4;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  if (!isDefined(vaultV1) || !isDefined(vaultAsset)) {
    let t2;
    if ($[11] !== vaultAddress) {
      t2 = abbreviateAddress(vaultAddress);
      $[11] = vaultAddress;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    const t3 = `Cannot display interaction with vault ${t2}`;
    let t4;
    if ($[13] !== t3) {
      t4 = <CardText height="240px" text={t3} />;
      $[13] = t3;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    return t4;
  }
  let t2;
  if ($[15] !== vaultV1.image) {
    t2 = <Avatar src={vaultV1.image} size={24} padding={2} />;
    $[15] = vaultV1.image;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      lineHeight: "normal",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis"
    };
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== vaultV1.name) {
    t4 = <span className="text-title-xs text-foreground" style={t3}>{vaultV1.name}</span>;
    $[18] = vaultV1.name;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <V1Badge />;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t2 || $[22] !== t4) {
    t6 = <div className="gap-xs flex items-center">{t2}{t4}{t5}</div>;
    $[21] = t2;
    $[22] = t4;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const t7 = VAULT_OPERATION_LABEL[type];
  let t8;
  if ($[24] !== amount || $[25] !== t7 || $[26] !== vaultAsset) {
    t8 = <RecapInteraction amount={amount} asset={vaultAsset} label={t7} />;
    $[24] = amount;
    $[25] = t7;
    $[26] = vaultAsset;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Separator />;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== vaultAsset.symbol) {
    t10 = <span className="text-secondary-foreground text-xs">Deposit ({vaultAsset.symbol})</span>;
    $[29] = vaultAsset.symbol;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== formattedInitialPosition) {
    t11 = <TooltipTrigger asChild={true}><span className="text-muted-foreground text-xs">{formattedInitialPosition}</span></TooltipTrigger>;
    $[31] = formattedInitialPosition;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== detailedFormattedInitialPosition) {
    t12 = !!detailedFormattedInitialPosition && <TooltipContent>{detailedFormattedInitialPosition}</TooltipContent>;
    $[33] = detailedFormattedInitialPosition;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t11 || $[36] !== t12) {
    t13 = <Tooltip>{t11}{t12}</Tooltip>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Icon className="text-foreground" icon={<ArrowRight />} />;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== detailedFormattedFinalPosition || $[40] !== formattedFinalPosition || $[41] !== isFinalPositionLoading) {
    t15 = isFinalPositionLoading ? <Skeleton className="h-(--text-xs--line-height) w-[56px]" /> : <Tooltip><TooltipTrigger asChild={true}><span className="text-foreground text-xs">{formattedFinalPosition}</span></TooltipTrigger>{!!detailedFormattedFinalPosition && <TooltipContent>{detailedFormattedFinalPosition}</TooltipContent>}</Tooltip>;
    $[39] = detailedFormattedFinalPosition;
    $[40] = formattedFinalPosition;
    $[41] = isFinalPositionLoading;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== finalPosition || $[44] !== isFinalPositionLoading || $[45] !== isSimulationFallback) {
    t16 = isSimulationFallback && !isFinalPositionLoading && isDefined(finalPosition) && <SimulationEstimateBadge />;
    $[43] = finalPosition;
    $[44] = isFinalPositionLoading;
    $[45] = isSimulationFallback;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t13 || $[48] !== t15 || $[49] !== t16) {
    t17 = <div className="flex items-center gap-[2px]">{t13}{t14}{t15}{t16}</div>;
    $[47] = t13;
    $[48] = t15;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t10 || $[52] !== t17) {
    t18 = <div className="flex items-center justify-between gap-[16px]">{t10}{t17}</div>;
    $[51] = t10;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="text-secondary-foreground text-xs">APY</span>;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== apy || $[56] !== netApy || $[57] !== performanceFee || $[58] !== points || $[59] !== rewards || $[60] !== vaultAsset) {
    t20 = {
      performanceFee,
      apy,
      variant: "vault",
      asset: vaultAsset,
      netApy,
      points,
      rewards
    };
    $[55] = apy;
    $[56] = netApy;
    $[57] = performanceFee;
    $[58] = points;
    $[59] = rewards;
    $[60] = vaultAsset;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== formattedNetApy) {
    t21 = <span className="text-foreground text-xs">{formattedNetApy}</span>;
    $[62] = formattedNetApy;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t20 || $[65] !== t21) {
    t22 = <div className="flex items-center justify-between gap-[16px]">{t19}<IconRewardHover RewardsHoverCardProps={t20}>{t21}</IconRewardHover></div>;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== t18 || $[68] !== t22) {
    t23 = <div className="gap-sm flex flex-col items-stretch">{t18}{t22}</div>;
    $[67] = t18;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== t23 || $[71] !== t8) {
    t24 = <div className="gap-sm flex flex-col items-stretch">{t8}{t9}{t23}</div>;
    $[70] = t23;
    $[71] = t8;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== t24 || $[74] !== t6) {
    t25 = <RecapContainer className="gap-lg flex-col">{t6}{t24}</RecapContainer>;
    $[73] = t24;
    $[74] = t6;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  return t25;
};
export default VaultV1Recap;